import { useQueryClient } from '@tanstack/react-query'
import { Copy, ExternalLink, Trash2, Undo2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { CreatePreviewDialog } from './create-preview-dialog'
import {
  buildPreviewCleanupRows,
  PREVIEW_ORG_TTL_DAYS,
  PreviewCleanupRow,
  PreviewCleanupState
} from './preview-cleanup-rows'

import { getOrganizationClient } from '@/api'
import { OrganizationCancelDeletionDialog } from '@/components/organization-cancel-deletion-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toast } from '@/components/ui/use-toast'
import { useQueryParams } from '@/hooks'
import { IOrganization } from '@/models'
import { useAuth } from '@/providers'
import { useOrganizationCleanup } from '@/providers/OrganizationCleanupContextProvider'
import { RoutePaths } from '@/routes/routes'
import { ORGANIZATION_QUERY_IDS, useQueryGetOrganizations } from '@/services'
import { isPermitted, Permissions } from '@/utils/permission-manager'

type Filter = 'all' | PreviewCleanupState

const STATE_LABELS: Record<PreviewCleanupState, string> = {
  active: 'Active',
  expired: `Expired (${PREVIEW_ORG_TTL_DAYS}+ days)`,
  queued: 'Queued for cleanup'
}

const STATE_VARIANTS: Record<
  PreviewCleanupState,
  'secondary' | 'destructive' | 'outline'
> = {
  active: 'secondary',
  expired: 'destructive',
  queued: 'outline'
}

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleString() : '-'

const SummaryTile = ({ label, value }: { label: string; value: number }) => (
  <Card className="bg-background">
    <CardHeader className="p-4 pb-1">
      <CardTitle className="text-sm text-muted-foreground">{label}</CardTitle>
    </CardHeader>
    <CardContent className="p-4 pt-0 text-xl font-semibold">
      {value}
    </CardContent>
  </Card>
)

const organizationClient = getOrganizationClient()

/**
 * Queues preview orgs for deletion (the same DELETE the org detail page uses:
 * the org goes to the cleanup table and the nightly job deletes it).
 */
const QueueCleanupDialog = ({
  rows,
  open,
  onOpenChange,
  onQueued
}: {
  rows: PreviewCleanupRow[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onQueued: () => void
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [input, setInput] = useState('')
  const confirmation =
    rows.length === 1
      ? rows[0].organization.name ?? String(rows[0].organization.id)
      : `${rows.length}`

  const close = (next: boolean) => {
    setInput('')
    onOpenChange(next)
  }

  const queue = async () => {
    setIsLoading(true)
    const results = await Promise.allSettled(
      rows.map((row) =>
        organizationClient.deleteOrganization({ org_id: row.organization.id! })
      )
    )
    const failed = results.filter((r) => r.status === 'rejected').length

    toast({
      title: failed
        ? `${rows.length - failed} of ${rows.length} preview orgs queued`
        : rows.length === 1
        ? 'Preview org queued for cleanup'
        : `${rows.length} preview orgs queued for cleanup`,
      description: failed
        ? `${failed} could not be queued. Try them again from the list.`
        : 'They are deleted by the next nightly cleanup run.'
    })
    setIsLoading(false)
    onQueued()
    close(false)
  }

  return (
    <Dialog onOpenChange={close} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-primary">
            {rows.length === 1
              ? `Queue "${rows[0].organization.name}" for cleanup?`
              : `Queue ${rows.length} expired preview orgs for cleanup?`}
          </DialogTitle>
          <DialogDescription>
            The nightly cleanup job deletes queued orgs and all their data.
            Until then, you can take an org out of the queue again.
          </DialogDescription>
        </DialogHeader>
        {rows.length > 1 && (
          <ul className="max-h-40 overflow-y-auto text-sm text-muted-foreground">
            {rows.map((row) => (
              <li key={row.organization.id}>
                {row.organization.name} ({row.organization.id})
              </li>
            ))}
          </ul>
        )}
        <div>
          <Label htmlFor="preview_cleanup_confirmation">
            Type &quot;{confirmation}&quot; to proceed
          </Label>
          <Input
            id="preview_cleanup_confirmation"
            onChange={(e) => setInput(e.target.value)}
            placeholder={confirmation}
            value={input}
          />
        </div>
        <DialogFooter>
          <Button
            disabled={input !== confirmation}
            loading={isLoading}
            onClick={queue}
            variant="destructive"
          >
            Queue for cleanup
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const PreviewOrgCleanup = () => {
  const queryClient = useQueryClient()
  const { userData } = useAuth()
  const canDelete = isPermitted(Permissions.DELETE_ORG, userData)

  const { data: organizations, isLoading: isLoadingOrganizations } =
    useQueryGetOrganizations()
  const { organizationsToCleanup, isLoading: isLoadingQueue } =
    useOrganizationCleanup()

  const canCreate = isPermitted(Permissions.CREATE_PREVIEW_ORG, userData)
  const navigate = useNavigate()
  // /organizations/preview-cleanup?create_from=<orgId> opens the create dialog
  // for that org (used by "Create preview" on the organization list)
  const { create_from: createFrom } = useQueryParams<{ create_from?: string }>()
  const [createOpen, setCreateOpen] = useState(false)
  const isCreateOpen = createOpen || Boolean(createFrom)
  const closeCreate = () => {
    setCreateOpen(false)
    if (createFrom)
      navigate(RoutePaths.ORGANIZATION_PREVIEW_CLEANUP, { replace: true })
  }

  const [filter, setFilter] = useState<Filter>('all')
  const [toQueue, setToQueue] = useState<PreviewCleanupRow[]>([])
  const [toCancel, setToCancel] = useState<IOrganization | undefined>()

  const rows = useMemo(
    () =>
      buildPreviewCleanupRows({
        organizations: organizations ?? [],
        cleanupQueue: organizationsToCleanup ?? []
      }),
    [organizations, organizationsToCleanup]
  )
  const counts = useMemo(
    () => ({
      active: rows.filter((r) => r.state === 'active').length,
      expired: rows.filter((r) => r.state === 'expired').length,
      queued: rows.filter((r) => r.state === 'queued').length
    }),
    [rows]
  )
  const visibleRows =
    filter === 'all' ? rows : rows.filter((r) => r.state === filter)
  const expiredRows = rows.filter((r) => r.state === 'expired')

  const refreshQueue = () =>
    queryClient.invalidateQueries({
      queryKey: [ORGANIZATION_QUERY_IDS.GET_ORGANIZATIONS_TO_CLEANUP]
    })

  const isLoading = isLoadingOrganizations || isLoadingQueue

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Preview org cleanup</h1>
          <p className="text-sm text-muted-foreground">
            Preview orgs are temporary copies of an org&apos;s configuration,
            created from Configuration Hub or here. They expire after{' '}
            {PREVIEW_ORG_TTL_DAYS} days. Queued orgs are deleted by the nightly
            cleanup job.
          </p>
        </div>
        <div className="flex gap-2">
          {canCreate && (
            <Button onClick={() => setCreateOpen(true)} variant="outline">
              <Copy className="mr-2 h-4 w-4" />
              Create preview
            </Button>
          )}
          {canDelete && (
            <Button
              disabled={!expiredRows.length}
              onClick={() => setToQueue(expiredRows)}
              variant="destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Queue {expiredRows.length} expired
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <SummaryTile label="Preview orgs" value={rows.length} />
        <SummaryTile label="Active" value={counts.active} />
        <SummaryTile label="Expired" value={counts.expired} />
        <SummaryTile label="Queued for cleanup" value={counts.queued} />
      </div>

      <Card className="bg-background">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <Tabs onValueChange={(v) => setFilter(v as Filter)} value={filter}>
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="expired">Expired</TabsTrigger>
              <TabsTrigger value="active">Active</TabsTrigger>
              <TabsTrigger value="queued">Queued</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Preview org</TableHead>
                <TableHead>Copy of</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Age</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell className="py-8 text-center" colSpan={6}>
                    <div className="h-4 animate-pulse rounded-lg bg-muted" />
                  </TableCell>
                </TableRow>
              ) : visibleRows.length ? (
                visibleRows.map((row) => (
                  <TableRow key={row.organization.id}>
                    <TableCell>
                      <Link
                        className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                        to={`${RoutePaths.ORGANIZATIONS}/${row.organization.id}`}
                      >
                        {row.organization.name || row.organization.id}
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                      <div className="text-xs text-muted-foreground">
                        {row.organization.id}
                      </div>
                    </TableCell>
                    <TableCell>
                      {row.organization.parent_production_org_id ? (
                        <>
                          <Link
                            className="text-blue-600 hover:underline"
                            to={`${RoutePaths.ORGANIZATIONS}/${row.organization.parent_production_org_id}`}
                          >
                            {row.parentName ||
                              row.organization.parent_production_org_id}
                          </Link>
                          <div className="text-xs text-muted-foreground">
                            {row.organization.parent_production_org_id}
                          </div>
                        </>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {formatDate(row.organization.created_date)}
                    </TableCell>
                    <TableCell>
                      {row.ageDays === undefined ? '-' : `${row.ageDays} d`}
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATE_VARIANTS[row.state]}>
                        {STATE_LABELS[row.state]}
                      </Badge>
                      {row.state === 'queued' && (
                        <div className="pt-1 text-xs text-muted-foreground">
                          since {formatDate(row.queuedAt)}
                          {row.queuedBy ? ` by ${row.queuedBy}` : ''}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {!canDelete ? null : row.state === 'queued' ? (
                        <Button
                          onClick={() => setToCancel(row.organization)}
                          size="sm"
                          variant="outline"
                        >
                          <Undo2 className="mr-2 h-4 w-4" />
                          Take out of queue
                        </Button>
                      ) : (
                        <Button
                          onClick={() => setToQueue([row])}
                          size="sm"
                          variant="outline"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Queue for cleanup
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    className="py-8 text-center text-muted-foreground"
                    colSpan={6}
                  >
                    No preview orgs here.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {canCreate && (
        <CreatePreviewDialog
          initialSourceOrgId={createFrom ? String(createFrom) : undefined}
          onOpenChange={(open) => !open && closeCreate()}
          onReady={() =>
            queryClient.invalidateQueries({
              queryKey: [ORGANIZATION_QUERY_IDS.GET_ORGANIZATIONS]
            })
          }
          open={isCreateOpen}
        />
      )}
      <QueueCleanupDialog
        onOpenChange={(open) => !open && setToQueue([])}
        onQueued={refreshQueue}
        open={toQueue.length > 0}
        rows={toQueue}
      />
      <OrganizationCancelDeletionDialog
        onCancelDeletion={() => {
          setToCancel(undefined)
          refreshQueue()
        }}
        onOpenChange={(open) => !open && setToCancel(undefined)}
        open={Boolean(toCancel)}
        organization={toCancel}
      />
    </div>
  )
}

export default PreviewOrgCleanup
